import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Sincroniza las variables CSS del chrome fijo móvil con las alturas REALES
 * renderizadas de `.app-header` y `.admin-tournament-context-nav`.
 *
 * Las constantes rem de styles.css (p. ej. `--torneos-mobile-header-height:
 * 6.78rem`) nunca pueden coincidir con la altura real en todos los
 * dispositivos (safe-area, wrapping de pills, fuentes). Este hook mide con
 * ResizeObserver y escribe los píxeles medidos como estilos inline, que
 * ganan la cascada, de modo que todos los calc() consumidores (top del nav
 * contextual, padding-top de .app-main, banner de impersonación) quedan
 * alineados con la realidad. El valor medido ya incluye el padding derivado
 * de env(safe-area-inset-top).
 *
 * Montar UNA sola vez en AppShell.
 */

const MOBILE_QUERY = "(max-width: 760px)";

/* La altura del header alimenta tanto la variable admin como la de modo
 * jugador: es el mismo elemento medido, solo cambia el nombre que consume
 * cada layout. */
const HEADER_VARS = [
  "--torneos-mobile-header-height",
  "--torneos-player-mobile-header-height",
] as const;

/* Ambas variantes reciben el valor medido: lo medido es lo real, aplique el
 * CSS la que aplique (con o sin fila de categoría). */
const CONTEXT_VARS = [
  "--torneos-mobile-context-height",
  "--torneos-mobile-context-height-with-category",
] as const;

const ALL_VARS = [...HEADER_VARS, ...CONTEXT_VARS];

type VarName = (typeof ALL_VARS)[number];

/* `--torneos-player-mobile-header-height` está declarada sobre
 * `.app-shell--player-mode` (no sobre :root), así que un inline en <html>
 * perdería frente a esa regla dentro del subárbol del shell. Escribimos en
 * ambos: documentElement (gana a :root) y el propio .app-shell (inline gana
 * a la regla de clase). */
function styleTargets(): HTMLElement[] {
  const targets: HTMLElement[] = [document.documentElement];
  const shell = document.querySelector<HTMLElement>(".app-shell");
  if (shell) targets.push(shell);
  return targets;
}

function setVars(names: readonly VarName[], value: string): void {
  for (const target of styleTargets()) {
    for (const name of names) {
      target.style.setProperty(name, value);
    }
  }
}

function removeVars(names: readonly VarName[]): void {
  for (const target of styleTargets()) {
    for (const name of names) {
      target.style.removeProperty(name);
    }
  }
}

export default function useFixedChromeHeights(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof window === "undefined" || typeof ResizeObserver === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const header = document.querySelector<HTMLElement>(".app-header");
    let contextNav = document.querySelector<HTMLElement>(".admin-tournament-context-nav");

    const sync = () => {
      /* Fuera de móvil (o sin header: landing pública) la hoja de estilos
       * vuelve a mandar. */
      if (!mediaQuery.matches || !header || !header.isConnected) {
        removeVars(ALL_VARS);
        return;
      }
      /* Math.ceil: redondear hacia arriba evita costuras de medio píxel
       * entre el header y el nav contextual. */
      const headerHeight = Math.ceil(header.getBoundingClientRect().height);
      setVars(HEADER_VARS, `${headerHeight}px`);
      if (contextNav && contextNav.isConnected) {
        const contextHeight = Math.ceil(contextNav.getBoundingClientRect().height);
        setVars(CONTEXT_VARS, `${contextHeight}px`);
      } else {
        removeVars(CONTEXT_VARS);
      }
    };

    const resizeObserver = new ResizeObserver(sync);
    if (header) resizeObserver.observe(header);
    if (contextNav) resizeObserver.observe(contextNav);

    /* El nav contextual monta/desmonta SIN cambio de ruta (aparece cuando
     * resuelve el fetch del torneo y desaparece si falla), así que vigilamos
     * los hijos directos del shell además de re-consultar por ruta. */
    const shell = document.querySelector<HTMLElement>(".app-shell");
    let mutationObserver: MutationObserver | null = null;
    if (shell) {
      mutationObserver = new MutationObserver(() => {
        const nextNav = document.querySelector<HTMLElement>(".admin-tournament-context-nav");
        if (nextNav === contextNav) return;
        if (contextNav) resizeObserver.unobserve(contextNav);
        contextNav = nextNav;
        if (contextNav) resizeObserver.observe(contextNav);
        sync();
      });
      mutationObserver.observe(shell, { childList: true });
    }

    mediaQuery.addEventListener("change", sync);
    sync();

    return () => {
      resizeObserver.disconnect();
      mutationObserver?.disconnect();
      mediaQuery.removeEventListener("change", sync);
      /* No borramos las variables entre rutas para evitar un parpadeo de
       * layout: la siguiente ejecución del efecto las reconcilia. */
    };
  }, [pathname]);

  /* Al desmontar el shell por completo, devolver el control a la hoja de
   * estilos. */
  useEffect(() => {
    return () => {
      removeVars(ALL_VARS);
    };
  }, []);
}
